/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, useState } from 'react';

import { css, jsx } from '@compiled/react';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import CheckmarkIcon from '@atlaskit/icon/core/check-mark';
import CrossIcon from '@atlaskit/icon/core/cross';
import ShortcutIcon from '@atlaskit/icon/core/link-external';
import Image from '@atlaskit/image';
import { Box, Flex, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token, useThemeObserver } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import type { ComponentAssets, GettingStartedCardProps, GifAssets, ImageAssets } from './types';

const cssStyles = cssMap({
	borderStyles: {
		borderRadius: token('radius.large'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
	},
	cardContainerStyles: {
		position: 'relative',
		maxHeight: '270px',
	},
	pressableStyles: {
		width: '100%',
		backgroundColor: token('elevation.surface.sunken'),
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		'&:hover': { backgroundColor: token('color.background.neutral.subtle.hovered') },
	},
	imageContainerStyles: {
		maxHeight: '128px',
		borderTopRightRadius: token('radius.large'),
		borderTopLeftRadius: token('radius.large'),
		overflow: 'hidden',
	},
	imageHiddenStyles: {
		display: 'none',
	},
	completedCardStyles: {
		opacity: token('opacity.disabled'),
	},
	contentContainerStyles: {
		maxHeight: '140px',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		gap: token('space.300'),
		alignSelf: 'stretch',
	},
	textStyles: {
		textAlign: 'left',
	},
	ctaStyles: {
		color: token('color.text.subtle'),
		gap: token('space.050'),
	},
	dismissIconStyles: {
		position: 'absolute',
		top: token('space.100'),
		right: token('space.100'),
		cursor: 'pointer',
	},
});

// This styling is used in case an image that is too small is passed in to fit the card width
const imageStyles = css({
	width: '100%',
	objectFit: 'cover',
});

const ellipsisTextStyles = css({
	display: '-webkit-box',
	overflow: 'hidden',
	whiteSpace: 'normal',
	textOverflow: 'ellipsis',
	WebkitBoxOrient: 'vertical',
});

//  This styling is for setting limits on text for heading and will truncate with ellipses
const headingTextOverflowStyles = css({
	maxHeight: '20px',
	WebkitLineClamp: 1, // Limits the number of lines to 1
});

const descriptionTextOverflowStyles = css({
	height: '32px',
});

export default function GettingStartedCard({
	image,
	heading,
	description,
	onClick,
	hrefLink,
	showAsExternalLink,
	onDismiss,
	ctaText,
	ctaCompletedText,
	dismissTooltip,
	headingSize,
	hasBorder,
	cardCompleted,
	enableCompletedOnClick = true,
}: GettingStartedCardProps) {
	const [gif, img, component] = [
		image as GifAssets,
		image as ImageAssets,
		image as ComponentAssets,
	];
	const isComponentImage = Boolean(component.component);

	const { colorMode } = useThemeObserver();
	const [hovered, setHovered] = useState(false);
	const [dismissed, setDismissed] = useState(false);
	const [hasClicked, setHasClicked] = useState(false);
	// Component image doens't require skeleton loading. Can set this to true straight away.
	const [imageLoaded, setImageLoaded] = useState(isComponentImage);
	const isCardCompleted = hasClicked || cardCompleted === true;

	// If showAsExternalLink is true, it will always be shown an external link, otherwise it will only be an external link if the hrefLink does not include 'wiki'.
	const isExternalLink = hrefLink && (showAsExternalLink ?? !hrefLink.includes('wiki'));

	const handleCardClick = (event: React.MouseEvent) => {
		if (onClick) {
			onClick(event);
		}
		if (hrefLink) {
			if (isExternalLink) {
				window.open(hrefLink, '_blank');
			} else {
				window.location.href = hrefLink;
			}
		}
		if (enableCompletedOnClick) {
			setHasClicked(true);
		}
	};
	const handleDismiss = () => {
		onDismiss();
		setDismissed(true);
	};

	if (dismissed) {
		return null;
	}

	let [imageSrc, darkModeImageSrc] = [img.src, img.srcDark || img.src];
	// Have dark mode default to light mode in case it was not passed in
	if (gif.gif && gif.staticImageGif) {
		if (hovered) {
			[imageSrc, darkModeImageSrc] = [gif.gif, gif.darkModeGif || gif.gif];
		} else {
			[imageSrc, darkModeImageSrc] = [
				gif.staticImageGif,
				gif.staticDarkModeImageGif || gif.staticImageGif,
			];
		}
	}
	let componentSrc;
	if (isComponentImage) {
		componentSrc = (colorMode === 'dark' && component.componentDark) || component.component;
	}

	const headingComponentSize = headingSize ? headingSize : 'small';

	return (
		<Box xcss={cssStyles.cardContainerStyles}>
			<Pressable
				onMouseEnter={() => setHovered(true)}
				onMouseLeave={() => setHovered(false)}
				onClick={handleCardClick}
				xcss={cx(
					cssStyles.pressableStyles,
					isCardCompleted && cssStyles.completedCardStyles,
					hasBorder && cssStyles.borderStyles,
				)}
			>
				<Stack>
					{!imageLoaded && (
						<Skeleton
							isShimmering
							height="128px"
							width="100%"
							borderRadius={token('radius.large')}
						/>
					)}
					{/* Hide image until its loaded to prevent both the skeleton and image appearing at the same time. */}
					<Flex
						alignItems="center"
						xcss={!imageLoaded ? cssStyles.imageHiddenStyles : cssStyles.imageContainerStyles}
					>
						{isComponentImage ? (
							componentSrc
						) : (
							<Image
								src={imageSrc}
								srcDark={darkModeImageSrc}
								alt=""
								css={imageStyles}
								onLoad={() => setImageLoaded(true)}
							/>
						)}
					</Flex>

					<Flex xcss={cssStyles.contentContainerStyles} alignItems="start" direction="column">
						<Flex direction="column" alignItems="start" gap="space.050" xcss={cssStyles.textStyles}>
							<Tooltip content={heading} isScreenReaderAnnouncementDisabled>
								<div css={[ellipsisTextStyles, headingTextOverflowStyles]}>
									<Heading size={headingComponentSize}>{heading}</Heading>
								</div>
							</Tooltip>
							<Tooltip content={description} isScreenReaderAnnouncementDisabled>
								<div css={[descriptionTextOverflowStyles]}>
									<Text maxLines={2} size="small" color="color.text.subtlest">
										{description}
									</Text>
								</div>
							</Tooltip>
						</Flex>
						<Flex alignItems="center" xcss={cssStyles.ctaStyles}>
							{isCardCompleted ? (
								<Fragment>
									<CheckmarkIcon label="" />
									<Text color="color.text.subtle">{ctaCompletedText}</Text>
								</Fragment>
							) : (
								<Fragment>
									<Text color="color.text.subtle">{ctaText}</Text>
									{isExternalLink && <ShortcutIcon label="" testId="shortcut-icon" />}
								</Fragment>
							)}
						</Flex>
					</Flex>
				</Stack>
			</Pressable>
			{hovered && (
				<Box
					xcss={cssStyles.dismissIconStyles}
					onMouseEnter={() => setHovered(true)}
					onMouseLeave={() => setHovered(false)}
				>
					<IconButton
						testId="button-dismiss"
						isTooltipDisabled={false}
						onClick={handleDismiss}
						label={dismissTooltip}
						appearance="subtle"
						icon={(iconProps) => <CrossIcon {...iconProps} />}
					/>
				</Box>
			)}
		</Box>
	);
}
